import { useEffect, useState } from 'react';
import { ProportionBar, StackedProportionBar } from './AnalyticsKpiBars';
import {
  getAdminClientKpis,
  readAdminClientKpisCache,
  writeAdminClientKpisCache,
} from '../../utils/adminAnalyticsKpis';

function AdminClientAnalyticsSummary({ clientId }) {
  const [kpis, setKpis] = useState(() => readAdminClientKpisCache(clientId));
  const [loading, setLoading] = useState(!readAdminClientKpisCache(clientId));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clientId) return undefined;
    let mounted = true;

    const run = async () => {
      const cached = readAdminClientKpisCache(clientId);
      if (cached) {
        setKpis(cached);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const data = await getAdminClientKpis(clientId);
        if (!mounted) return;
        writeAdminClientKpisCache(clientId, data);
        setKpis(data);
      } catch {
        if (mounted) setError('Could not load client metrics');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => { mounted = false; };
  }, [clientId]);

  const fmt = (key) => {
    if (loading && !kpis) return '…';
    return (kpis?.[key] ?? 0).toLocaleString();
  };

  const bootcampSegments = [
    { key: 'scheduled', label: 'Scheduled', value: kpis?.bootcampsScheduled ?? 0, color: '#f59e0b' },
    { key: 'active', label: 'Active', value: kpis?.bootcampsActive ?? 0, color: '#10b981' },
    { key: 'completed', label: 'Completed', value: kpis?.bootcampsCompleted ?? 0, color: '#64748b' },
  ];

  return (
    <div className="analytics-kpi-section">
      {error && <div className="modern-error modern-mb-4">{error}</div>}

      <div className="analytics-kpi-grid">
        <div className="analytics-kpi-card">
          <div className="analytics-kpi-label">Regions</div>
          <div className="analytics-kpi-value">{fmt('regions')}</div>
        </div>

        <div className="analytics-kpi-card">
          <div className="analytics-kpi-label">Teams</div>
          <div className="analytics-kpi-value">{fmt('teams')}</div>
        </div>

        <div className="analytics-kpi-card analytics-kpi-card-with-bar">
          <div className="analytics-kpi-label">Users</div>
          <div className="analytics-kpi-value">{fmt('users')}</div>
          <ProportionBar
            filled={kpis?.activeUsers ?? 0}
            total={kpis?.users ?? 0}
            label={loading && !kpis ? '…' : `${(kpis?.activeUsers ?? 0).toLocaleString()} active (${kpis?.users ? Math.round(((kpis.activeUsers || 0) / kpis.users) * 100) : 0}%)`}
          />
        </div>

        <div className="analytics-kpi-card analytics-kpi-card-with-bar">
          <div className="analytics-kpi-label">Bootcamps</div>
          <div className="analytics-kpi-value">{fmt('bootcamps')}</div>
          <StackedProportionBar segments={bootcampSegments} total={kpis?.bootcamps ?? 0} />
        </div>

        <div className="analytics-kpi-card">
          <div className="analytics-kpi-label">Checkpoints Completed</div>
          <div className="analytics-kpi-value">{fmt('checkpointsCompleted')}</div>
        </div>

        <div className="analytics-kpi-card">
          <div className="analytics-kpi-label">Reflections Written</div>
          <div className="analytics-kpi-value">{fmt('reflectionsWritten')}</div>
        </div>
      </div>
    </div>
  );
}

export default AdminClientAnalyticsSummary;
